import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { generateId } from './constants';

const inputStyle = (extra = {}) => ({
  width: '100%', background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12,
  padding: '11px 14px', fontSize: 14, color: 'white',
  outline: 'none', fontFamily: 'inherit', ...extra
});

function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function ClientModal({ client, onSave, onClose }) {
  const isEdit = !!(client && client.name);
  const [form, setForm] = useState(client || { name: '', address: '', phone: '', notes: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#181b22', width: '100%', maxWidth: 480,
        borderRadius: 20, padding: 24
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: 'white' }}>{isEdit ? 'Edit Client' : 'New Client'}</h2>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.08)',
            border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 18
          }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Client Name *">
            <input autoFocus style={inputStyle()} placeholder="e.g. Harbor View Hotel"
              value={form.name} onChange={e => set('name', e.target.value)} />
          </Field>
          <Field label="Address">
            <input style={inputStyle()} placeholder="123 Main St, City, State"
              value={form.address} onChange={e => set('address', e.target.value)} />
          </Field>
          <Field label="Phone">
            <input style={inputStyle()} placeholder="(555) 000-0000"
              value={form.phone} onChange={e => set('phone', e.target.value)} />
          </Field>
          <Field label="Notes">
            <textarea style={{ ...inputStyle(), resize: 'none', minHeight: 80, lineHeight: 1.5 }}
              placeholder="Job site notes, access codes, contact info..."
              value={form.notes} onChange={e => set('notes', e.target.value)} />
          </Field>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={{
            padding: 13, borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)',
            background: 'transparent', color: 'rgba(255,255,255,0.5)',
            fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
          }}>Cancel</button>
          <button onClick={() => { if (!form.name.trim()) return; onSave(form); onClose(); }}
            disabled={!form.name.trim()} style={{
              padding: 13, borderRadius: 12, border: 'none',
              background: form.name.trim() ? 'white' : 'rgba(255,255,255,0.15)',
              color: form.name.trim() ? '#0e1015' : 'rgba(255,255,255,0.3)',
              fontSize: 14, fontWeight: 700, cursor: form.name.trim() ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit'
            }}>{isEdit ? 'Save Changes' : 'Add Client'}</button>
        </div>
      </div>
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { loadClients(); }, []);

  const loadClients = async () => {
    setLoading(true);
    const { data } = await supabase.from('clients').select('*').order('name', { ascending: true });
    setClients(data || []);
    setLoading(false);
  };

  const saveClient = async (form) => {
    if (form.id) {
      await supabase.from('clients').update(form).eq('id', form.id);
      setClients(prev => prev.map(c => c.id === form.id ? { ...c, ...form } : c));
    } else {
      const { data } = await supabase.from('clients').insert({ ...form, id: generateId() }).select().single();
      if (data) { setClients(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name))); setExpanded(data.id); }
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm('Delete this client and all their floor plans?')) return;
    await supabase.from('clients').delete().eq('id', id);
    setClients(prev => prev.filter(c => c.id !== id));
    if (expanded === id) setExpanded(null);
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 }}>
      <div className="spin" style={{ width: 28, height: 28, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: 'white', borderRadius: '50%' }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '36px 20px', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'white' }}>Clients</h1>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
            {clients.length} client{clients.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button onClick={() => { setEditClient(null); setShowModal(true); }} style={{
          padding: '8px 16px', borderRadius: 10, border: 'none',
          background: 'white', color: '#0e1015', fontWeight: 700,
          fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
          display: 'flex', alignItems: 'center', gap: 6
        }}>+ New Client</button>
      </div>

      {clients.length === 0 ? (
        <div style={{
          border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 20,
          padding: '60px 20px', textAlign: 'center'
        }}>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14, marginBottom: 16 }}>No clients yet</p>
          <button onClick={() => setShowModal(true)} style={{
            padding: '10px 22px', borderRadius: 12, border: 'none',
            background: 'white', color: '#0e1015', fontWeight: 700,
            fontSize: 13, cursor: 'pointer', fontFamily: 'inherit'
          }}>+ Add first client</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {clients.map(client => (
            <div key={client.id} style={{
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 16, overflow: 'hidden'
            }}>
              {/* Client row */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px 16px', cursor: 'pointer'
              }} onClick={() => setExpanded(expanded === client.id ? null : client.id)}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: 'rgba(255,255,255,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 15, fontWeight: 700, color: 'white'
                }}>{client.name.charAt(0).toUpperCase()}</div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'white' }}>{client.name}</div>
                  {client.address && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{client.address}</div>}
                  {client.phone && !client.address && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{client.phone}</div>}
                </div>

                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={e => { e.stopPropagation(); setEditClient(client); setShowModal(true); }} style={{
                    width: 30, height: 30, borderRadius: 8, border: 'none',
                    background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  </button>
                  <button onClick={e => { e.stopPropagation(); deleteClient(client.id); }} style={{
                    width: 30, height: 30, borderRadius: 8, border: 'none',
                    background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                  <div style={{
                    width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'rgba(255,255,255,0.25)', transition: 'transform 0.2s',
                    transform: expanded === client.id ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Expanded detail */}
              {expanded === client.id && (
                <div style={{ padding: '0 16px 16px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  {(client.phone || client.address || client.notes) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 14, marginBottom: 14 }}>
                      {client.phone && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', width: 60, flexShrink: 0, paddingTop: 1 }}>Phone</span>
                          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{client.phone}</span>
                        </div>
                      )}
                      {client.address && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', width: 60, flexShrink: 0, paddingTop: 1 }}>Address</span>
                          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{client.address}</span>
                        </div>
                      )}
                      {client.notes && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', width: 60, flexShrink: 0, paddingTop: 1 }}>Notes</span>
                          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{client.notes}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <ClientModal
          client={editClient}
          onSave={saveClient}
          onClose={() => { setShowModal(false); setEditClient(null); }}
        />
      )}
    </div>
  );
}
